import { lazy } from 'react';
import type { AppManifest } from '@/apps/types';

/**
 * Windows Media Player.
 *
 * A singleton, as it was: XP let you open a second copy, but every shortcut
 * and every double-clicked file went to the one that was already running, and
 * two players fighting over the sound card is nobody's idea of a good time.
 *
 * The playlist is in `src/config/media.ts`: mp3s under `public/assets/media/`,
 * or — for a track that carries note data instead — a piece performed on a
 * couple of oscillators by `./synth.ts`.
 *
 * Args:
 *   track     string | number   track id, or index into the playlist.
 *   autoplay  boolean           start it, rather than only select it. The
 *                               shell sets this when a sound file was
 *                               activated, because opening one has always
 *                               meant playing it; the double-click is also
 *                               the gesture browsers want before any audio.
 *                               Launched from the Start menu, the player
 *                               still comes up stopped.
 */
export const mediaPlayerApp: AppManifest = {
  id: 'mediaplayer',
  name: 'Windows Media Player',
  icon: '/assets/icons/mediaplayer.svg',
  component: lazy(() => import('./MediaPlayer')),

  defaultSize: { w: 480, h: 420 },
  minSize: { w: 320, h: 300 },
  resizable: true,
  maximizable: true,
  singleton: true,

  // XP handed Media Player every sound file on the disk, and kept `.mp3`
  // until something else asked for it.
  openWith: ['.mp3'],

  group: 'Accessories',
  titleFor: () => 'Windows Media Player',
};
